import { useCallback, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import {
  GetUserGroupsDocument,
  GetUserGroupsQuery,
  useAddUserGroupMembersMutation,
  useCreateUserGroupMutation,
  useDeleteUserGroupMutation,
  useGetUserGroupsQuery,
  useRemoveUserGroupMembersMutation,
  useUpdateUserGroupMutation,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { dummyUserGroups } from '@/data/dummy';

export interface UserGroupItem {
  id: string;
  name: string;
  description?: string | null;
  memberUserIds: string[];
  /** Non-null for auto-managed system groups (read-only rosters). */
  systemKey?: string | null;
}

interface MutationResult {
  success: boolean;
  error?: string;
}

type RawGroup = NonNullable<GetUserGroupsQuery['userGroups']>[number];

function toItem(group: RawGroup): UserGroupItem {
  return {
    // Backend ids are Int; normalize to string at the hook boundary so grant
    // group-ids (ID strings) compare with ===.
    id: String(group.id),
    name: group.name || '',
    description: group.description,
    memberUserIds: (group.memberUserIds || []).map(String),
    systemKey: group.systemKey,
  };
}

/** Fixed sentinel id served in preview mode (dummy groups carry no implementation groups). */
const PREVIEW_PRIVATE_SENTINEL_ID = '59';

/**
 * The workspace's `private` sentinel system group id, or null while loading /
 * when sharing was never enabled (the group is provisioned with the feature).
 * A grant to this member-less group is how the grid popover writes the
 * "Private — only you can see this" state (the record's owner always retains
 * access via acl_owner_ids). Queried with includeSystem: true — the default
 * userGroups listing deliberately hides implementation groups — and cached
 * separately from useUserGroups' listing by its distinct variables.
 */
export function usePrivateSentinelGroupId(opts?: { skip?: boolean }) {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();

  const { data, loading } = useGetUserGroupsQuery({
    variables: { workspaceId: workspaceId || '', includeSystem: true },
    skip: !!opts?.skip || preview || !workspaceId,
    fetchPolicy: 'cache-first',
  });

  const privateSentinelGroupId = useMemo(() => {
    if (preview) return PREVIEW_PRIVATE_SENTINEL_ID;
    const sentinel = (data?.userGroups || []).find((group) => group.systemKey === 'private');
    return sentinel ? String(sentinel.id) : null;
  }, [preview, data]);

  return { privateSentinelGroupId, loading: preview ? false : loading };
}

/**
 * Workspace user groups — the audiences records can be shared with.
 * Dual-mode: preview serves editable local copies of `dummyUserGroups`;
 * production talks to the userGroups GraphQL surface. The backend's default
 * listing already includes the auto-managed "Admins" system group and hides
 * the manager-of / private implementation groups.
 *
 * Cache strategy (no refetchQueries outside the settings page): create/delete
 * splice the getUserGroups cache entry via cache.updateQuery; update/add/remove
 * return the full UserGroupFields fragment, which merges by UserGroupType:id.
 */
export function useUserGroups() {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();

  const [previewGroups, setPreviewGroups] = useState<UserGroupItem[]>(() =>
    dummyUserGroups.map((group) => ({ ...group, memberUserIds: [...group.memberUserIds] }))
  );

  const { data, loading, error, refetch } = useGetUserGroupsQuery({
    variables: { workspaceId: workspaceId || '' },
    skip: preview || !workspaceId,
    fetchPolicy: 'cache-and-network',
  });

  const [createMutation] = useCreateUserGroupMutation({
    update(cache, { data: payload }) {
      const created = payload?.createUserGroup?.userGroup;
      if (!created || !workspaceId) return;
      cache.updateQuery<GetUserGroupsQuery>(
        { query: GetUserGroupsDocument, variables: { workspaceId } },
        (existing) =>
          existing?.userGroups
            ? { ...existing, userGroups: [...existing.userGroups, created] }
            : existing
      );
    },
  });
  const [updateMutation] = useUpdateUserGroupMutation();
  const [deleteMutation] = useDeleteUserGroupMutation();
  const [addMembersMutation] = useAddUserGroupMembersMutation();
  const [removeMembersMutation] = useRemoveUserGroupMembersMutation();

  const allGroups = useMemo<UserGroupItem[]>(() => {
    if (preview) return previewGroups;
    return (data?.userGroups || []).map(toItem);
  }, [preview, previewGroups, data]);

  const groups = useMemo(() => allGroups.filter((g) => !g.systemKey), [allGroups]);
  const systemGroups = useMemo(() => allGroups.filter((g) => !!g.systemKey), [allGroups]);

  const createGroup = useCallback(
    async (name: string, description?: string, memberUserIds?: string[]): Promise<MutationResult> => {
      if (preview) {
        setPreviewGroups((prev) => [
          ...prev,
          {
            id: String(Date.now()),
            name,
            description,
            memberUserIds: memberUserIds || [],
            systemKey: null,
          },
        ]);
        return { success: true };
      }
      if (!workspaceId) return { success: false, error: 'No workspace selected' };
      try {
        await createMutation({ variables: { workspaceId, name, description, memberUserIds } });
        return { success: true };
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Failed to create group' };
      }
    },
    [preview, workspaceId, createMutation]
  );

  const updateGroup = useCallback(
    async (id: string, patch: { name?: string; description?: string }): Promise<MutationResult> => {
      if (preview) {
        setPreviewGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
        return { success: true };
      }
      if (!workspaceId) return { success: false, error: 'No workspace selected' };
      try {
        await updateMutation({ variables: { workspaceId, id, ...patch } });
        return { success: true };
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Failed to update group' };
      }
    },
    [preview, workspaceId, updateMutation]
  );

  const deleteGroup = useCallback(
    async (id: string): Promise<MutationResult> => {
      if (preview) {
        setPreviewGroups((prev) => prev.filter((g) => g.id !== id));
        return { success: true };
      }
      if (!workspaceId) return { success: false, error: 'No workspace selected' };
      try {
        await deleteMutation({
          variables: { workspaceId, id },
          update(cache) {
            cache.updateQuery<GetUserGroupsQuery>(
              { query: GetUserGroupsDocument, variables: { workspaceId } },
              (existing) =>
                existing?.userGroups
                  ? {
                      ...existing,
                      userGroups: existing.userGroups.filter((g) => String(g.id) !== id),
                    }
                  : existing
            );
          },
        });
        return { success: true };
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Failed to delete group' };
      }
    },
    [preview, workspaceId, deleteMutation]
  );

  /** Save a group's whole roster: diffed into add + remove member calls. */
  const setGroupMembers = useCallback(
    async (id: string, userIds: string[]): Promise<MutationResult> => {
      if (preview) {
        setPreviewGroups((prev) =>
          prev.map((g) => (g.id === id ? { ...g, memberUserIds: [...userIds] } : g))
        );
        return { success: true };
      }
      if (!workspaceId) return { success: false, error: 'No workspace selected' };
      const current = allGroups.find((g) => g.id === id)?.memberUserIds || [];
      const toAdd = userIds.filter((uid) => !current.includes(uid));
      const toRemove = current.filter((uid) => !userIds.includes(uid));
      try {
        if (toAdd.length) {
          await addMembersMutation({ variables: { workspaceId, groupId: id, userIds: toAdd } });
        }
        if (toRemove.length) {
          await removeMembersMutation({
            variables: { workspaceId, groupId: id, userIds: toRemove },
          });
        }
        return { success: true };
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Failed to update members' };
      }
    },
    [preview, workspaceId, allGroups, addMembersMutation, removeMembersMutation]
  );

  return {
    /** Ordinary (editable, shareable) groups. */
    groups,
    /** Auto-managed system groups (read-only rosters; "Admins" is a share target). */
    systemGroups,
    /** Both, in backend listing order. */
    allGroups,
    loading: preview ? false : loading,
    error: preview ? null : ((error as ApolloError | undefined) ?? null),
    createGroup,
    updateGroup,
    deleteGroup,
    setGroupMembers,
    refetch: preview ? () => Promise.resolve(undefined) : refetch,
  };
}
